import React from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Printer, ArrowLeft } from "lucide-react";
import { useGetReceiptQuery } from "../../api/receiptApi";

type PaymentStatus = "paid" | "pending" | "failed";

const WATERMARK_POSITIONS = [
  { top: "3%", left: "2%" },
  { top: "3%", left: "42%" },
  { top: "3%", right: "8%" },
  { top: "22%", left: "22%" },
  { top: "40%", left: "2%" },
  { top: "40%", right: "8%" },
  { top: "58%", left: "42%" },
  { top: "70%", left: "2%" },
  { top: "78%", right: "8%" },
  { top: "88%", left: "22%" },
];

export default function ReceiptPage() {
  const { paymentId } = useParams();
  const navigate = useNavigate();
  const { data, isLoading } = useGetReceiptQuery(paymentId!);
console.log(data, "his is the datas!!!!!!")
  const formatAmount = (amount: any) => {
    const val = parseFloat(amount) || 0;
    return (val / 100).toLocaleString("en-NG", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
  };

  const toNairaWords = (amount: any) => `(=N=${formatAmount(amount)})`;

  /**
   * The calendar date a payment was made, in the Institute's timezone.
   *
   * Was `new Date(x).toISOString().split("T")[0]`, which renders the instant in
   * UTC. Nigeria is UTC+1, so a payment recorded at midnight on the 2nd came
   * back as 23:00 on the 1st and the receipt printed the wrong day.
   *
   * Resolved against Africa/Lagos rather than by slicing the string, because
   * the API sends two shapes depending on how far the backend has been
   * deployed — `…T23:00:00Z` and `…T00:00:00+01:00` — and slicing is only
   * correct for the second. Naming the timezone is right for both, and for a
   * bare `YYYY-MM-DD` too.
   */
  const formatDate = (dateString: string | null) => {
    if (!dateString) return null;

    const d = new Date(dateString);
    if (Number.isNaN(d.getTime())) return String(dateString).slice(0, 10);

    // en-CA formats as YYYY-MM-DD, which is what the receipt prints.
    return d.toLocaleDateString("en-CA", { timeZone: "Africa/Lagos" });
  };

  if (isLoading) {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen gap-4 bg-gray-100">
        <div className="w-8 h-8 border-2 border-blue-900 border-t-transparent rounded-full animate-spin" />
        <span className="text-[10px] font-bold uppercase tracking-[0.4em] text-gray-400">
          Loading receipt...
        </span>
      </div>
    );
  }

  const status: PaymentStatus =
    data?.status === "paid" ? "paid" : data?.status === "failed" ? "failed" : "pending";
  const isPaid = status === "paid";
  const formattedDate = formatDate(data?.date ?? null);
  const outstandingBalance = data?.invoice?.balance ?? 0;
  const isPartial = data?.invoice?.status === "partial" || outstandingBalance > 0;
  const advanceAmount = (data?.items ?? []).find((item: any) => item.is_advance)?.amount_paid ?? 0;
  const hasAdvance = advanceAmount > 0;

  const formatPaymentMethod = (method: string | null | undefined) => {
    if (!method) return "Transfer";
    return method.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
  };

  const fields: { label: string; value: React.ReactNode }[] = [
    {
      label: "Receipt No",
      value: data?.receipt_number ? (
        <span className="font-mono text-[11px] font-bold tracking-wide">{data.receipt_number}</span>
      ) : null,
    },
    { label: "Date", value: formattedDate },
    {
      label: "Payment Year",
      value: data?.year ? <span className="font-semibold">{data.year}</span> : null,
    },
    { label: "Payment Mode", value: isPaid ? formatPaymentMethod(data?.payment_method) : null },
    // A non-member pays under the name they registered with and has no
    // membership number — their registration number identifies them instead,
    // so the row becomes that rather than printing an empty one.
    data?.membership_no
      ? { label: "Membership No", value: data.membership_no }
      : {
          label: data?.registration_no ? "Registration No" : "Membership No",
          value: data?.registration_no ?? null,
        },
    { label: "Receiving Bank", value: isPaid ? (data?.bank?.name ?? "—") : null },
    {
      label: "Received From",
      value: data?.user ? <span className="uppercase">{data.user}</span> : null,
    },
    {
      label: "",
      value: data?.amount ? <span className="font-semibold">{toNairaWords(data.amount)}</span> : null,
    },
  ];

  const renderPerf = () => (
    <div className="absolute right-0 top-0 bottom-0 flex flex-col justify-between py-1 z-10 print-perf" style={{ width: "18px" }}>
      {Array.from({ length: 36 }).map((_, i) => (
        <div key={i} className="rounded-full bg-gray-200" style={{ width: "14px", height: "14px", flexShrink: 0 }} />
      ))}
    </div>
  );

  const renderWatermarks = () =>
    WATERMARK_POSITIONS.map((pos, i) => (
      <div key={i} className="absolute pointer-events-none select-none" style={{ ...pos, opacity: 0.07, zIndex: 0 }}>
        <img src="/niqs-logo.png" alt="" className="w-20 h-20 object-contain" onError={(e) => { e.currentTarget.style.display = "none"; }} />
      </div>
    ));

  return (
    <div className="p-4 md:p-10 bg-gray-200 min-h-screen print:min-h-0 print:bg-white print:p-0 font-sans">
      <style>{`
        @media print {
          @page { size: A4 landscape; margin: 0; }
          html, body { height: 100% !important; overflow: visible !important; background: white; }
          body * { visibility: hidden; }
          #print-layout, #print-layout * { visibility: visible; }
          #print-layout { display: flex !important; width: 297mm; min-height: 100%; position: absolute; left: 0; top: 0; }
          
          /* Force the 3 identical synchronized columns side by side */
          .triptych-col { flex: 0 0 99mm; width: 99mm; min-height: 100%; background: white; position: relative; box-sizing: border-box; }
          
          /* Prevent rows from cleanly splitting inside a cell across pages ugly */
          tr { page-break-inside: avoid; break-inside: avoid; }
        }
      `}</style>

      {/* Control Buttons */}
      <div className="mx-auto print:hidden mb-5 flex justify-between items-center" style={{ maxWidth: "374px" }}>
        <button
          onClick={() => navigate(-1)}
          className="flex items-center gap-2 text-[11px] font-bold uppercase tracking-widest text-gray-500 hover:text-gray-900 transition-colors group"
        >
          <ArrowLeft className="w-3.5 h-3.5 group-hover:-translate-x-1 transition-transform" />
          Back
        </button>
        <button
          onClick={() => window.print()}
          className="flex items-center gap-2 px-4 py-2 bg-white border border-gray-200 rounded-lg text-[11px] font-bold uppercase tracking-wider text-gray-600 hover:bg-gray-50 shadow-sm transition-all"
        >
          <Printer className="w-3.5 h-3.5" />
          Print
        </button>
      </div>

      {/* Shared Layout Structure for Screen View AND Print View */}
      <div id="print-layout" className="mx-auto bg-white shadow-2xl print:shadow-none flex flex-row" style={{ maxWidth: "374px", width: "100%" }}>
        
        {/* Render columns ([0] for preview on web, [0, 1, 2] automatically side-by-side during print layout) */}
        {[0, 1, 2].map((colIndex) => (
          <div 
            key={colIndex} 
            className={`triptych-col relative ${colIndex > 0 ? "hidden print:block" : "w-full"}`}
          >
            {renderWatermarks()}
            {renderPerf()}
            
            <div className="relative pb-6" style={{ zIndex: 1, paddingRight: "26px" }}>
              {status !== "paid" && (
                <div className={`text-center py-2 text-[10px] font-black uppercase tracking-widest border-b ${status === "pending" ? "bg-amber-50 text-amber-700 border-amber-200" : "bg-red-50 text-red-700 border-red-200"}`}>
                  {status === "pending" ? "Payment Pending — Receipt issued upon confirmation" : "Payment Failed — Transaction could not be completed"}
                </div>
              )}
              
              {/* Header */}
              <div className="flex flex-col items-center pt-6 pb-4 px-6 border-b border-gray-300">
                <img src="/niqs-logo.png" alt="NIQS" className="w-16 h-16 object-contain" onError={(e) => { e.currentTarget.style.display = "none"; }} />
              </div>

              {/* Identity Metadata Fields */}
              <div className="px-6 divide-y divide-gray-100">
                {fields.map((row, i) => (
                  <div key={i} className="flex items-start py-2.5 gap-3">
                    <span className="text-[12px] text-gray-700 w-36 flex-shrink-0 font-black">{row.label}</span>
                    <span className="text-[12px] text-gray-900 font-bold flex-1">{row.value ?? <span className="text-gray-300">—</span>}</span>
                  </div>
                ))}
              </div>

              {/* Dynamic Flow Table */}
              <div className="px-6 pt-4 pb-2">
                <table className="w-full text-[11px]">
                  <thead>
                    <tr className="border-b border-gray-200">
                      <th className="text-left py-2 px-3 font-black text-gray-500 w-10">S/No</th>
                      <th className="text-left py-2 px-3 font-black text-gray-500">Item Purpose of Receipt</th>
                      <th className="text-left py-2 px-3 font-black text-gray-500 w-16">Year</th>
                      <th className="text-right py-2 px-3 font-black text-gray-500">Amount (₦)</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data?.items && data.items.length > 0 ? (
                      data.items.map((item: any, i: number) => (
                        <tr key={i} className={item.is_advance ? "bg-emerald-50" : ""}>
                          <td className={`py-2.5 px-3 font-black ${item.is_advance ? "text-emerald-700" : "text-gray-800"}`}>{i + 1}</td>
                          <td className={`py-2.5 px-3 font-black ${item.is_advance ? "text-emerald-800" : "text-gray-900"}`}>
                            {item.is_advance ? "Advance" : item.description}
                            {item.is_advance && (
                              <span className="ml-1 text-[9px] font-black uppercase tracking-wider bg-emerald-100 text-emerald-700 px-1 py-0.5 rounded">advance</span>
                            )}
                          </td>
                          <td className={`py-2.5 px-3 font-black ${item.is_advance ? "text-emerald-600" : "text-gray-800"}`}>{item.year ?? "—"}</td>
                          <td className={`py-2.5 px-3 text-right font-mono font-black ${item.is_advance ? "text-emerald-700" : "text-gray-900"}`}>{formatAmount(item.amount_paid)}</td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan={4} className="py-5 text-center text-gray-400 text-[11px] italic">
                          {status === "pending" ? "Awaiting payment confirmation" : "No items — transaction was not completed"}
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>

              {/* Bottom Financial Footers */}
              <div className="mt-4">
                <div className="px-6 pb-1">
                  <div className="flex justify-between items-center border border-gray-400 py-2.5 px-3">
                    <span className="text-[11px] font-black uppercase tracking-wider text-gray-800">AMOUNT PAID</span>
                    <span className={`text-[13px] font-black font-mono ${isPaid ? "text-gray-900" : status === "pending" ? "text-amber-700" : "text-red-700"}`}>
                      {formatAmount(data?.amount)}
                    </span>
                  </div>
                </div>
                {isPartial && outstandingBalance > 0 && (
                  <div className="px-6 pb-2">
                    <div className="flex justify-between items-center border border-amber-400 bg-amber-50 py-2.5 px-3">
                      <span className="text-[11px] font-black uppercase tracking-wider text-amber-800">OUTSTANDING BALANCE</span>
                      <span className="text-[13px] font-black font-mono text-amber-700">{formatAmount(outstandingBalance)}</span>
                    </div>
                    <p className="text-[9px] text-amber-600 text-right mt-0.5 pr-1">Partial payment — balance remains due</p>
                  </div>
                )}
                <div className="text-center py-4 px-6">
                  <p className="text-[9px] uppercase tracking-[0.35em] text-gray-400 font-semibold">Computer-Generated Receipt · NIQS Registry</p>
                </div>
              </div>

            </div>
          </div>
        ))}
      </div>
    </div>
  );
}